import { Badge, LuvImage } from '@/components/ui';
import classNames from 'classnames';
import { UniversalCta } from '../UniversalCta';
import { TypeColumnHalf } from '@/core/generated-types/TypeTwoColumnHalf';
import HeaderText, { HeaderSize } from '@/components/ui/HeaderText/HeaderText';

export interface ColumnHalfProps extends TypeColumnHalf {
  isSecondHalf?: boolean;
}

const TwoColumnHalf: React.FC<ColumnHalfProps> = ({ fields, isSecondHalf }) => {
  const {
    image,
    imageProportion,
    badge,
    title,
    paragraph,
    cta,
    list,
    listImage,
    boxedText,
    boxedImage,
  } = fields;

  return (
    <div
      id={isSecondHalf ? 'secondHalf' : 'firstHalf'}
      className="w-full lg:w-1/2 h-full flex justify-center items-center"
    >
      {image ? (
        <div
          className={classNames({
            'w-full h-full flex items-center justify-center': true,
            'lg:justify-end': !isSecondHalf,
            'lg:justify-start': isSecondHalf,
            'px-6 mb-8 lg:mb-0 lg:pl-36 lg:pr-0':
              !isSecondHalf && imageProportion === 'inset',
            'px-6 lg:pr-36 lg:pl-0':
              isSecondHalf && imageProportion === 'inset',
          })}
        >
          <LuvImage
            className={classNames({
              'w-full':
                imageProportion === 'inset' || imageProportion === 'fullBleed',
              'h-full': imageProportion === 'fullBleed',
            })}
            imageData={image}
            alt={image.fields.altText}
            Image={image.fields.Image}
          />
        </div>
      ) : (
        <div
          className={classNames({
            'w-full flex flex-col px-6': true,
            'lg:pl-36 lg:pr-14 mb-6 lg:mb-0': !isSecondHalf,
            'lg:pr-36 lg:pl-14': isSecondHalf,
          })}
        >
          {badge && (
            <Badge
              text={badge}
              badgeColor="IndigoDark"
              classes="bg-dark mb-2"
            />
          )}
          {title && (
            <HeaderText
              size={HeaderSize.H2}
              text={title}
              classes="text-charcoal-v2 mb-1 lg:mb-4"
              hoverEffect
            />
          )}
          {paragraph && (
            <p className="text-base lg:text-xl text-charcoal-v2">{paragraph}</p>
          )}
          {list && (
            <ul className="flex flex-col justify-start items-start gap-4 mt-6">
              {list.map((item: string) => {
                return (
                  <p
                    key={item}
                    className="flex text-lg font-normal lg:text-xl lg:font-bold text-charcoal-v2"
                  >
                    {listImage && (
                      <LuvImage
                        className="w-[18px] lg:w-5 h-[18px] lg:h-5 mr-2"
                        imageData={listImage}
                        alt={listImage.fields.altText}
                        Image={listImage.fields.Image}
                      />
                    )}
                    {item}
                  </p>
                );
              })}
            </ul>
          )}
          {boxedText && (
            <div className="w-full flex justify-start items-center px-4 py-[18px] border-2 border-yellow rounded-lg mt-6">
              {boxedImage && (
                <LuvImage
                  className="w-12 h-12 mr-6"
                  imageData={boxedImage}
                  alt={boxedImage.fields.altText}
                />
              )}
              <p className="text-sm font-bold text-charcoal-v2">{boxedText}</p>
            </div>
          )}
          {cta && (
            <div className="flex items-center justify-start mt-6 lg:mt-8">
              <UniversalCta link={cta} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoColumnHalf;
